/* eslint-disable no-nested-ternary */
/* eslint-disable react/forbid-prop-types */
/* eslint-disable react/jsx-props-no-spreading */
import React, { useEffect, useMemo, useRef } from 'react';
import { useSelector } from 'react-redux';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import { useFormState } from 'react-hook-form';
import DoubleCurrencyField from './components/DoubleCurrencyField';
import FxRateApply from './components/FxRateApply';
import Button from '../../../../elements/Button';

function Step5PayoutsCurrentPrognosis({ index, updateWorkflowStatus, form, signalNext, setSignalNext }) {
  const { t: tT } = useTranslation('app', { keyPrefix: 'schema.pension.transaction' });
  const { t: tA } = useTranslation('app', { keyPrefix: 'schema.pension.account' });
  const { t: tI } = useTranslation('addAccount', { keyPrefix: 'pension' });

  const baseCurrency = useSelector((state) => state.user.profile.settings.baseCurrency);

  const { register, watch, getValues, setValue, trigger, setError, clearErrors } = form;
  const { errors } = useFormState({
    control: form.control,
    name: [
      'payoutOnlyContributionsToDateQuote',
      'payoutOnlyContributionsToDateIndexedQuote',
      'payoutAllPlannedContributionsQuote',
      'payoutAllPlannedContributionsIndexedQuote',
      'payoutLumpSumVerrentetQuote',
      'quoteGroup',
      'useSpecialQuote',
    ],
  });

  // register a background field to the form
  useEffect(() => {
    register('useSpecialQuote');
    register('quoteGroup');
  }, [register]);

  const watchedCurrency = watch('currency');
  const accountCurrency = watchedCurrency || baseCurrency;
  const isBaseCurrencySameAsAccountCurrency = getValues('isBaseCurrencySameAsAccountCurrency'); // set in step 1
  const useSpecialQuote = watch('useSpecialQuote');

  const quoteFieldsNames = ['payoutOnlyContributionsToDate', 'payoutOnlyContributionsToDateIndexed', 'payoutAllPlannedContributions', 'payoutAllPlannedContributionsIndexed', 'payoutLumpSumVerrentet'];

  const quoteFieldsValues = watch(quoteFieldsNames.map((field) => `${field}Quote`));
  const quoteFieldsBaseValues = watch(quoteFieldsNames.map((field) => `${field}QuoteBaseCurrency`));

  //------------------------------------------------------------------------------
  // output logic
  //------------------------------------------------------------------------------

  const buildInitialQuotes = (formValues) => {
    const quotes = [];
    quoteFieldsNames.forEach((val) => {
      const quoteValue = formValues[`${val}Quote`];
      if (quoteValue !== undefined && quoteValue !== null && quoteValue !== '') {
        const quote = Number(quoteValue);
        if (Number.isNaN(quote)) return;
        const baseValue = isBaseCurrencySameAsAccountCurrency ? quote : Number(formValues[`${val}QuoteBaseCurrency`]);
        quotes.push({
          type: val,
          quote,
          quoteBaseCurrency: Number.isNaN(baseValue) ? quote : baseValue,
        });
      }
    });
    return quotes;
  };

  const processOutputData = (formValues) => {
    const result = { ...formValues };
    result.initialQuotes = buildInitialQuotes(formValues);
    return result;
  };

  const outputsEqual = (a, b) => JSON.stringify(a) === JSON.stringify(b);
  const prevOutputRef = useRef(processOutputData(getValues()));

  //--------------------------------------------------------------------------------
  // logic to require at least one of the values to be filled out
  //--------------------------------------------------------------------------------

  const hasValue = (value) => value !== undefined && value !== null && value !== '' && value !== 0 && value !== '0';

  const quoteKey = quoteFieldsValues.map((v) => v ?? '').join('|'); // so that we don't rerun the effect every time there is a render
  const quoteBaseKey = quoteFieldsBaseValues.map((v) => v ?? '').join('|');
  useEffect(() => {
    const hasAtLeastOneQuote = isBaseCurrencySameAsAccountCurrency
      ? quoteFieldsValues.some((value) => hasValue(value))
      : quoteFieldsNames.some((_, idx) => hasValue(quoteFieldsValues[idx]) && hasValue(quoteFieldsBaseValues[idx]));

    const stepIsValid = hasAtLeastOneQuote && Boolean(useSpecialQuote);
    if (!hasAtLeastOneQuote) {
      setError('quoteGroup', { type: 'manual', message: tI('payoutsAmountValidation') });
      clearErrors('useSpecialQuote');
    } else {
      clearErrors('quoteGroup');
      if (!useSpecialQuote) {
        setError('useSpecialQuote', { type: 'manual', message: tI('payoutsAmountUseSpecialQuoteValidation') });
      } else {
        clearErrors('useSpecialQuote');
      }
    }

    const output = processOutputData(getValues());
    if (!outputsEqual(output, prevOutputRef.current)) {
      setValue('initialQuotes', output.initialQuotes, { shouldDirty: true, shouldValidate: false });
      prevOutputRef.current = output;
    }
    updateWorkflowStatus(index, stepIsValid);
  }, [quoteKey, quoteBaseKey, useSpecialQuote, isBaseCurrencySameAsAccountCurrency]);

  // auto-select the first quote that is ready

  const hasAutoSelectedSpecialQuote = useRef(false);

  useEffect(() => {
    if (hasAutoSelectedSpecialQuote.current) return;
    if (useSpecialQuote) {
      hasAutoSelectedSpecialQuote.current = true;
      return;
    }
    const firstFilledIndex = quoteFieldsNames.findIndex((_, idx) => {
      if (isBaseCurrencySameAsAccountCurrency) return hasValue(quoteFieldsValues[idx]);
      return hasValue(quoteFieldsValues[idx]) && hasValue(quoteFieldsBaseValues[idx]);
    });
    if (firstFilledIndex >= 0) {
      const selectedKey = quoteFieldsNames[firstFilledIndex];
      setValue('useSpecialQuote', selectedKey, { shouldDirty: true, shouldValidate: false });
      clearErrors('useSpecialQuote');
      hasAutoSelectedSpecialQuote.current = true;
    }
  }, [quoteFieldsBaseValues, quoteFieldsNames, quoteFieldsValues, useSpecialQuote, isBaseCurrencySameAsAccountCurrency]);

  // reset useSpecialQuote if the user deletes the value
  useEffect(() => {
    if (!useSpecialQuote) return;

    const activeIndex = quoteFieldsNames.indexOf(useSpecialQuote);
    if (activeIndex === -1) return;

    const accountValue = quoteFieldsValues[activeIndex];
    const baseValue = quoteFieldsBaseValues[activeIndex];
    const hasAccountValue = hasValue(accountValue);
    const hasBaseValue = hasValue(baseValue);
    const isFilled = isBaseCurrencySameAsAccountCurrency ? hasAccountValue : hasAccountValue && hasBaseValue;
    if (!isFilled) {
      setValue('useSpecialQuote', '', { shouldDirty: true, shouldValidate: false });
    }
  }, [quoteFieldsValues, quoteFieldsBaseValues, isBaseCurrencySameAsAccountCurrency]);

  // handle signalNext
  useEffect(() => {
    if (signalNext) {
      const fieldsToValidate = [
        ...quoteFieldsNames.map((field) => `${field}Quote`),
        ...(!isBaseCurrencySameAsAccountCurrency ? quoteFieldsNames.map((field) => `${field}QuoteBaseCurrency`) : []),
        'quoteGroup',
        'useSpecialQuote',
      ];
      trigger(fieldsToValidate);
      setSignalNext(false);
    }
  }, [signalNext, trigger, setSignalNext, quoteFieldsNames, isBaseCurrencySameAsAccountCurrency]);

  // handle the "apply fx rate" button
  const fxRate = watch('fxRate');

  function handleFxRateButton() {
    // get a list of dirtyFields and filter them to only leave fieldNames which have payoutAll or payoutOnly and which end in Quote
    // if that list is not empty, apply the fxRate to those fields
    if (!Number.isFinite(fxRate)) return;
    quoteFieldsNames.forEach((fieldName) => {
      const fieldQuote = getValues(`${fieldName}Quote`);
      if (fieldQuote) {
        setValue(`${fieldName}QuoteBaseCurrency`, Number((fxRate * fieldQuote).toFixed(2)), { shouldValidate: true });
      }
    });
  }

  // renders the appropriate action button

  const rowStateByField = useMemo(
    () => quoteFieldsNames.map((key, x) => {
      const accountValue = quoteFieldsValues[x];
      const baseValue = quoteFieldsBaseValues[x];
      const hasAccountValue = hasValue(accountValue);
      const hasBaseValue = hasValue(baseValue);
      const isFilled = isBaseCurrencySameAsAccountCurrency ? hasAccountValue : hasAccountValue && hasBaseValue;
      const isActive = useSpecialQuote === key;
      return { key, isFilled, isActive };
    }),
    [isBaseCurrencySameAsAccountCurrency, quoteFieldsBaseValues, quoteFieldsNames, quoteFieldsValues, useSpecialQuote],
  );

  function setActiveQuote(fieldName) {
    setValue('useSpecialQuote', fieldName, { shouldDirty: true, shouldValidate: false });
  }

  function renderAction(fieldName, isFilled, isActive, breakpointHidden = false) {
    if (isActive) {
      return <span className="inline-flex items-center gap-2 text-sm font-semibold text-brandBlue-700">{tI('wizardSteps.payoutsAmount.useThisActive')}</span>;
    }
    if (!isFilled) return null;
    const visibilityClasses = breakpointHidden ? 'hidden md:flex' : 'flex md:hidden';
    return (
      <div className={`${visibilityClasses}`}>
        <Button text={tI('wizardSteps.payoutsAmount.useThisCta')} onClick={() => setActiveQuote(fieldName)} />
      </div>
    );
  }

  //---------------------------------------------------------------------------------------------
  // render the form
  //---------------------------------------------------------------------------------------------

  return (
    <div className="pt-4 sm:pt-0">
      <div className="mb-4">
        <h3 className="text-xl md:text-2xl font-bold leading-6 text-gray-900">{tI('wizardSteps.payoutsAmount.title')}</h3>
        <p className="mt-1 pb-4 text-sm text-gray-500">{tI('wizardSteps.payoutsAmount.subtitle')}</p>
      </div>

      <div className="w-full md:w-3/4 lg:w-2/3 bg-white rounded-lg shadow-sm divide-y divide-gray-200">
        {rowStateByField.map(({ key, isFilled, isActive }) => {
          const rowBase = 'group px-4 md:px-5 py-4 md:py-5 transition-colors';
          const rowClass = isActive ? `${rowBase} bg-brandBlue-50` : `${rowBase} bg-white`;
          const labelClassName = isActive ? 'text-brandBlue-900 font-semibold' : isFilled ? 'text-gray-800' : 'text-gray-600';
          return (
            <div key={key} className={rowClass}>
              <div className="grid grid-cols-1 md:grid-cols-[minmax(0,1fr)_160px] gap-3 md:items-start">
                <DoubleCurrencyField
                  errors={errors}
                  register={register}
                  watch={watch}
                  accountCurrency={accountCurrency}
                  isBaseCurrencySameAsAccountCurrency={isBaseCurrencySameAsAccountCurrency}
                  baseCurrency={baseCurrency}
                  fieldName={key}
                  label={tT(`${key}.label`)}
                  tooltip={tT(`${key}.description`)}
                  optionalText={null}
                  className="md:col-span-1"
                  labelClassName={labelClassName}
                />
                <div className="hidden md:flex items-start justify-end pt-1">{renderAction(key, isFilled, isActive, true)}</div>
              </div>
              <div className="mt-3 md:hidden">{renderAction(key, isFilled, isActive, false)}</div>
            </div>
          );
        })}
      </div>
      {/* FXRATE BUTTON (if fxRate is a number, display this button) */}

      {!isBaseCurrencySameAsAccountCurrency && (
        <FxRateApply
          className="mt-4 xs:w-1/2"
          fxRate={Number(fxRate)}
          labelText={tA('fxRate.label', { accountCurrency, baseCurrency })}
          tooltipText={tA('fxRate.tooltip', { baseCurrency })}
          buttonText={tA('fxRate.apply', { baseCurrency })}
          // eslint-disable-next-line react/jsx-no-bind
          onApply={handleFxRateButton}
        />
      )}
    </div>
  );
}

Step5PayoutsCurrentPrognosis.propTypes = {
  index: PropTypes.number.isRequired,
  updateWorkflowStatus: PropTypes.func.isRequired,
  form: PropTypes.object.isRequired,
  signalNext: PropTypes.bool,
  setSignalNext: PropTypes.func,
};
Step5PayoutsCurrentPrognosis.defaultProps = {
  signalNext: undefined,
  setSignalNext: () => {},
};

export default Step5PayoutsCurrentPrognosis;
